import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@repo/shared-client/components/ui'
import { useTranslation } from '@web/src/lib/intl/react'
import type { ComponentRef } from 'react'
import { useRef, useState } from 'react'

import MediaUploader from '../media-uploader/media-uploader'

type MediaUploaderDialogProps = {
  open?: boolean
  onOpenChange?: (details: { open: boolean }) => void
  modal?: boolean
  disablePointerDismissal?: boolean
  disableVideo?: boolean
  eventId?: string
  groupId?: string
  albumId?: string
  placeId?: string
  publicationId?: string
  vendorMarketId?: string
  vendorMarketItemId?: string
  onUploadComplete?: () => void
}

const MediaUploaderDialog = ({
  disableVideo,
  eventId,
  groupId,
  albumId,
  placeId,
  publicationId,
  vendorMarketId,
  vendorMarketItemId,
  onUploadComplete,
  open,
  onOpenChange,
  modal,
  disablePointerDismissal,
}: MediaUploaderDialogProps) => {
  const { t } = useTranslation()
  const [selectedMedia, setSelectedMedia] = useState(0)
  const mediaUploader = useRef<ComponentRef<typeof MediaUploader>>(null)
  const [showSpinner, setShowSpinnerValue] = useState(false)

  const closeDialog = () => {
    onOpenChange?.({ open: false })
  }

  const handleClose = () => {
    closeDialog()
  }

  const handleUploadFailure = () => {
    setShowSpinnerValue(false)
  }

  const handleUploadsCompleted = () => {
    setShowSpinnerValue(false)

    if (onUploadComplete) {
      onUploadComplete()
    }

    closeDialog()
  }

  return (
    <Dialog
      disablePointerDismissal={disablePointerDismissal}
      modal={modal}
      onOpenChange={(nextOpen) => onOpenChange?.({ open: nextOpen })}
      open={open}
    >
      <DialogContent
        className="flex max-h-[90dvh] min-h-0 max-w-md flex-col gap-0 overflow-hidden p-0 sm:max-w-lg"
        initialFocus={false}
        showCloseButton
      >
        <DialogHeader className="shrink-0 px-4 pt-4">
          <DialogTitle>{t('Upload Media')}</DialogTitle>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain px-4 py-4">
          <div className="flex w-full flex-col items-stretch">
            <MediaUploader
              disableVideo={disableVideo}
              onMediaSelected={setSelectedMedia}
              onUploadFailure={handleUploadFailure}
              onUploadsCompleted={() => handleUploadsCompleted()}
              ref={mediaUploader}
            />
          </div>
        </div>
        <DialogFooter className="shrink-0 gap-2 px-8 pb-8">
          <Button disabled={showSpinner} onClick={handleClose} type="button" variant="outline">
            {t('Cancel')}
          </Button>
          <Button
            disabled={selectedMedia === 0 || showSpinner}
            onClick={() => {
              setShowSpinnerValue(true)
              mediaUploader.current?.performUpload({
                albumId,
                eventId,
                groupId,
                placeId,
                publicationId,
                vendorMarketId,
                vendorMarketItemId,
              })
            }}
            type="button"
            variant="default"
          >
            {showSpinner ? <Spinner className="size-4" /> : t('Upload')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default MediaUploaderDialog
